import AnchorBubbleChart from '../components/AnchorBubbleChart';
import { AnchorTextcolumns as columns } from '../columnsConfig';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import ApiLoadError from '@/components/errors/ApiLoadError';
import { useMemo } from 'react';
import {
  useGetAnchorTextCountQuery,
  useGetAnchorTextMutation,
  useGetNumOfAnchorTextsChartQuery
} from '../api/backlinkAnchorText';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useExportBacklinkTableMutation } from '../api/exportApi';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import useBacklinkQueryBody, {
  toOverviewQueryBody
} from '../hooks/useBacklinkQueryBody';


type AnchorFilterColumn = {
  operator?: string;
  title?: string;
  filterModes?: { mode?: string; value?: unknown }[];
};

/** Drop UI-only random keys so the query cache stays stable when the URL does not change. */
function stableFilters(
  filters: Record<string, AnchorFilterColumn> | undefined
): Record<string, AnchorFilterColumn> {
  if (!filters) return {};
  return Object.fromEntries(
    Object.entries(filters).map(([column, value]) => [
      column,
      {
        operator: value?.operator,
        title: value?.title ?? '',
        filterModes: (value?.filterModes ?? []).map(({ mode, value: modeValue }) => ({
          mode,
          value: modeValue
        }))
      }
    ])
  );
}

function anchorFilterFromSearch(params: URLSearchParams): string | null {
  const raw = params.get('FByAnchor');
  if (!raw) return null;
  const match = raw.match(/\[(.+?),(.+?),(.+?)\]/);
  if (!match) return null;
  if (match[2] === 'blank') return '';
  try {
    return decodeURIComponent(match[3] ?? '');
  } catch {
    return match[3] ?? '';
  }
}

const AnchorTextPage = () => {
  const { domain } = useParams();
  const { pathname } = useLocation();
  const [exportTable] = useExportBacklinkTableMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const body = useBacklinkQueryBody();
  const filters = useMemo(
    () => stableFilters(body.filters as Record<string, AnchorFilterColumn>),
    [searchParams]
  );
  const chartFilters = useMemo(() => {
    const next = { ...filters };
    delete next.anchor;
    return next;
  }, [filters]);
  const overviewBody = toOverviewQueryBody({ ...body, filters: chartFilters });
  const tableBody = { ...body, filters };
  const selectedRaw = anchorFilterFromSearch(searchParams);
  const onSelectAnchor = (raw: string) => {
    const next = new URLSearchParams(searchParams);
    if (selectedRaw === raw) {
      next.delete('FByAnchor');
    } else if (raw === '') {
      next.set('FByAnchor', '[and,blank,null]');
    } else {
      next.set('FByAnchor', `[and,equals,${encodeURIComponent(raw)}]`);
    }
    setSearchParams(next);
  };
  const {
    data: getAnchorTextsChartRes,
    isFetching: getAnchorTextsChartLoading,
    isError: getAnchorTextsChartError,
    error: getAnchorTextsChartErrorDetails,
    refetch: refetchAnchorTextsChart
  } = useGetNumOfAnchorTextsChartQuery({ ...overviewBody, limit: 50 });
  const {
    data: getAnchorTextsRes,
    isFetching: getAnchorTextLoading,
    isError: getAnchorTextError,
    error: getAnchorTextErrorDetails,
    refetch: refetchAnchorText
  } = useGetAnchorTextCountQuery({ ...tableBody, page: 1, perPage: 50 });
  const [
    getAnchorTextsFunc,
    {
      isError: isAnchorTextsMutationError,
      error: anchorTextsMutationError,
      isLoading: isAnchorTextsMutationLoading,
      reset: resetAnchorTextsMutation
    }
  ] = useGetAnchorTextMutation();
  return (
    <div>
      <FiltersComponent
        preQueries={searchParams.toString()}
        columns={columns}
      />
      {!getAnchorTextsChartLoading ? (
        getAnchorTextsChartError ? (
          <ApiLoadError
            error={getAnchorTextsChartErrorDetails}
            isError={getAnchorTextsChartError}
            onRetry={() => refetchAnchorTextsChart?.()}
            isRetrying={getAnchorTextsChartLoading}
          />
        ) : (
          <div className="mb-3">
            <AnchorBubbleChart
              data={getAnchorTextsChartRes?.data}
              domain={domain}
              selectedRaw={selectedRaw}
              onSelect={onSelectAnchor}
            />
          </div>
        )
      ) : (
        <></>
      )}
      {!getAnchorTextLoading ? (
        getAnchorTextError ? (
          <ApiLoadError
            error={getAnchorTextErrorDetails}
            isError={getAnchorTextError}
            onRetry={() => refetchAnchorText?.()}
            isRetrying={getAnchorTextLoading}
          />
        ) : isAnchorTextsMutationError ? (
          <ApiLoadError
            error={anchorTextsMutationError}
            isError={isAnchorTextsMutationError}
            onRetry={() => {
              resetAnchorTextsMutation?.();
              refetchAnchorText?.();
            }}
            isRetrying={isAnchorTextsMutationLoading}
          />
        ) : (
          <AgGridWrapper
            table_name={pathname.split('/').slice(1, 3).join('/')}
            otherOptions={{
              headerHeight: 52
            }}
            settings={{
              pagination: true,
              fullscreen: false,
              changeHeight: false,
              resetGrid: false,
              saveGrid: false,
              sideBar: false,
              export: {
                enabled: true,
                fetchFunction: exportTable,
                additionalData: tableBody
              }
            }}
            stickyHorizontalScroll
            stickyHeader
            height="auto"
            fetchFunction={getAnchorTextsFunc}
            total={getAnchorTextsRes?.data?.total}
            data={getAnchorTextsRes?.data?.rows}
            columns={columns}
            additionalData={tableBody}
          />
        )
      ) : (
        <AgGridSkeleton height="400px" />
      )}
    </div>
  );
};

export default AnchorTextPage;
